/**
 * Tokens visuais da legenda didática.
 *
 * A legenda é configurável de propósito: instrutores diferentes usam
 * convenções diferentes. Nenhum componente 3D deve escrever cor literal.
 */

export type CategoriaDidatica =
  | 'estrutura'
  | 'zonaDeformacao'
  | 'srs'
  | 'retencao'
  | 'carroceria'
  | 'habitaculo'
  | 'mecanica'
  | 'energia'
  | 'vitima'

export const CORES_SEMANTICAS = {
  /** estrutura rígida / reforços */
  estrutura: '#e2514b',
  /** zonas de absorção/deformação */
  zonaDeformacao: '#f0c24b',
  /** SRS / airbags */
  srs: '#4aa6ff',
  /** cinto / pretensionador / retenção */
  retencao: '#ff8d3f',
  /** trajetória ou rota de retirada */
  trajetoria: '#4fd18b',
  /** eixo anatômico / coluna da vítima */
  eixoAnatomico: '#a67bff',
  /** carroceria não selecionada */
  carroceria: '#8ba0ad',
  habitaculo: '#5c6f7d',
  /** conjunto motriz: motor, transmissão, direção, escapamento */
  mecanica: '#9aa6b2',
  /**
   * fontes de energia e seus caminhos: combustível, gás, alta tensão, bateria.
   *
   * Reusa o âmbar-alaranjado que a norma já associa a cabo de alta tensão, em
   * vez de trazer mais uma cor para a legenda disputar atenção.
   */
  energia: '#ff9f1c',
  vitima: '#d8a27c',
  vitimaRoupa: '#2f4a63',
  /** realce de seleção */
  selecao: '#ffffff',
  /** vetor de impacto */
  impacto: '#ff5a52',
  /** estrutura que obstrui a rota de retirada */
  obstrucao: '#ff3b6b',
  /** rastro de movimento do ocupante */
  rastro: '#7fe3ff',
  /** achado clínico revelado do XABCDE: destaque discreto, não é risco nem diagnóstico */
  achado: '#7fe3ff',
  /** seta cervical: desvio dentro da faixa considerada alinhada */
  cervicalAlinhado: '#3ddc84',
  /** seta cervical: faixa de atenção */
  cervicalAtencao: '#f0c24b',
  /** seta cervical: desvio excessivo */
  cervicalExcessivo: '#ff3b6b',
  /**
   * Faixas de giro da rota de retirada.
   *
   * Reusam os valores já validados da faixa cervical em vez de trazer uma
   * paleta nova: verde é o que a equipe quer, vermelho é o que ela evita, e o
   * aluno já aprendeu esse vocabulário na seta cervical. `giroAxial` usa o
   * verde-água da trajetória porque retirada axial não é só "pouco giro": é
   * nenhum, e merece ser distinguível do leve.
   */
  giroAxial: '#4fd18b',
  giroLeve: '#3ddc84',
  giroModerado: '#f0c24b',
  giroAcentuado: '#ff3b6b',
} as const

/** Cor de cada faixa de giro, para arco 3D e painel não divergirem. */
export const CORES_FAIXA_GIRO = {
  axial: CORES_SEMANTICAS.giroAxial,
  leve: CORES_SEMANTICAS.giroLeve,
  moderado: CORES_SEMANTICAS.giroModerado,
  acentuado: CORES_SEMANTICAS.giroAcentuado,
} as const

/**
 * Tons de cinza da carroceria.
 *
 * A legenda mantém a carroceria em cinza, mas peças vizinhas com exatamente a
 * mesma cor viram um bloco só: estas variações mínimas preservam a leitura de
 * "peças separadas" sem competir com as cores semânticas.
 */
export const TONS_CARROCERIA = {
  painelLateral: '#8ba0ad',
  capo: '#96abb8',
  teto: '#9db2be',
  porta: '#7d919e',
  tampaTraseira: '#8799a6',
  paraChoque: '#687a87',
  vidroMoldura: '#71838f',
} as const

/**
 * Paleta automotiva realista PBR para iluminação, acabamentos e veículo colidente.
 */
export const CORES_AUTOMOTIVAS = {
  /** Pintura azul marinho metálico sólido para o veículo-alvo (colidente/atingido) */
  alvoCarroceria: '#1e3a8a',
  alvoCapo: '#1d4ed8',
  alvoTeto: '#172554',
  alvoVidroFume: '#628ca6',
  /** Faróis dianteiros */
  farolLente: '#f1f5f9',
  farolProjetor: '#ffffff',
  farolEmissivo: '#e0f2fe',
  farolRefletor: '#e2e8f0',
  farolPisca: '#f59e0b',
  /** Lanternas traseiras */
  lanternaLente: '#b91c1c',
  lanternaLed: '#dc2626',
  lanternaEmissivo: '#ef4444',
  lanternaRe: '#f8fafc',
  /** Rodas e freios */
  rodaPneu: '#18181b',
  rodaAro: '#e2e8f0',
  rodaAroAlvo: '#94a3b8',
  discoFreio: '#64748b',
  pincaFreio: '#ef4444',
  /** Grade e acabamentos */
  gradeColmeia: '#0f172a',
  gradeMoldura: '#334155',
  espelhoRetrovisor: '#cbd5e1',
} as const

export const COR_POR_CATEGORIA: Record<CategoriaDidatica, string> = {
  estrutura: CORES_SEMANTICAS.estrutura,
  zonaDeformacao: CORES_SEMANTICAS.zonaDeformacao,
  srs: CORES_SEMANTICAS.srs,
  retencao: CORES_SEMANTICAS.retencao,
  carroceria: CORES_SEMANTICAS.carroceria,
  habitaculo: CORES_SEMANTICAS.habitaculo,
  mecanica: CORES_SEMANTICAS.mecanica,
  energia: CORES_SEMANTICAS.energia,
  vitima: CORES_SEMANTICAS.vitima,
}

export interface EntradaLegenda {
  categoria: CategoriaDidatica | 'trajetoria' | 'eixoAnatomico' | 'cervical'
  cor: string
  texto: string
}

export const LEGENDA: EntradaLegenda[] = [
  { categoria: 'estrutura', cor: CORES_SEMANTICAS.estrutura, texto: 'estrutura rígida / reforços' },
  { categoria: 'zonaDeformacao', cor: CORES_SEMANTICAS.zonaDeformacao, texto: 'zonas de absorção' },
  { categoria: 'srs', cor: CORES_SEMANTICAS.srs, texto: 'SRS / airbags' },
  { categoria: 'retencao', cor: CORES_SEMANTICAS.retencao, texto: 'cinto / pretensionador' },
  { categoria: 'trajetoria', cor: CORES_SEMANTICAS.trajetoria, texto: 'rota de retirada' },
  { categoria: 'eixoAnatomico', cor: CORES_SEMANTICAS.eixoAnatomico, texto: 'eixo da coluna' },
  { categoria: 'cervical', cor: CORES_SEMANTICAS.cervicalAlinhado, texto: 'seta cervical (verde/âmbar/vermelho por faixa)' },
  { categoria: 'mecanica', cor: CORES_SEMANTICAS.mecanica, texto: 'conjunto motriz' },
  { categoria: 'energia', cor: CORES_SEMANTICAS.energia, texto: 'energia: combustível, gás, alta tensão' },
  { categoria: 'carroceria', cor: CORES_SEMANTICAS.carroceria, texto: 'carroceria / não selecionado' },
]

/**
 * Rótulos curtos exibidos na cena.
 *
 * `confirmadaPeloInstrutor: false` significa que a abreviação ainda não foi
 * validada doutrinariamente. Nesse caso a cena não mostra a sigla — mostra
 * apenas o nome completo no painel de detalhes. Isso evita inventar sigla.
 */
export interface RotuloConfiguravel {
  sigla: string
  confirmadaPeloInstrutor: boolean
}

export const ROTULOS: Record<string, RotuloConfiguravel> = {
  colunaA: { sigla: 'A', confirmadaPeloInstrutor: true },
  colunaB: { sigla: 'B', confirmadaPeloInstrutor: true },
  colunaC: { sigla: 'C', confirmadaPeloInstrutor: true },
  srs: { sigla: 'SRS', confirmadaPeloInstrutor: true },
  pretensionador: { sigla: 'PT', confirmadaPeloInstrutor: false },
  zonaReforco: { sigla: 'ZR', confirmadaPeloInstrutor: false },
}

/** Retorna a sigla apenas quando ela já foi confirmada pelo instrutor. */
export function siglaVisivel(chave: string): string | null {
  const rotulo = ROTULOS[chave]
  if (!rotulo || !rotulo.confirmadaPeloInstrutor) return null
  return rotulo.sigla
}
